/**
 * The Markt per country in Mira (Hans, Track #2611022; decided by Taimoor on 2026-11-04). IRES Cloud
 * publishes a Markt je Projekt for every country with at least five companies and lists them on the
 * all-countries document (`countries`). IRES users choose one in the comparison menu upwards, as an entry
 * beside "Markt": "Markt Deutschland", "Markt Österreich". The comparison stays the Markt; only the document
 * it reads changes.
 */

/** The prefix of a country's entry in the comparison menu upwards: `MARKET_COUNTRY:DE`. */
const MARKET_COUNTRY_OPTION_PREFIX = "MARKET_COUNTRY:";

/** A country's entry in the comparison menu upwards. */
export function marketCountryOption(country: string): string {
  return `${MARKET_COUNTRY_OPTION_PREFIX}${country}`;
}

/** The country of a menu entry, or `null` for any other entry. */
export function marketCountryOf(option: string): string | null {
  return option.startsWith(MARKET_COUNTRY_OPTION_PREFIX) ? option.slice(MARKET_COUNTRY_OPTION_PREFIX.length) : null;
}

/**
 * A country's name in the user's language ("Deutschland", "Austria"), from the browser, so no list of
 * countries is kept here; the code itself where the browser cannot name it.
 */
export function countryName(country: string, locale: string): string {
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(country) ?? country;
  } catch (error) {
    // An unknown code or an older browser without Intl.DisplayNames: the code is still readable.
    return country;
  }
}

/**
 * The comparison menu upwards with a country's Markt after "Markt" for each country offered (#2611022).
 *
 * @param options - the comparisons upwards, "Markt" among them where it is offered.
 * @param marketOption - the "Markt" option.
 * @param countries - the countries the user may narrow the Markt to (none for a client's users).
 */
export function withMarketCountryOptions(options: readonly string[], marketOption: string, countries: readonly string[]): string[] {
  const menu = [...options];
  const marketIndex = menu.indexOf(marketOption);
  if (marketIndex >= 0) {
    menu.splice(marketIndex + 1, 0, ...countries.map(marketCountryOption));
  }
  return menu;
}

/** The country of the Markt in effect: the chosen one where it is still offered, else `null` (every country). */
export function effectiveMarketCountry(stored: string | null, countries: readonly string[]): string | null {
  return stored && countries.includes(stored) ? stored : null;
}

/** "Markt Deutschland" for a country's menu entry, else `null` (any other entry is named as before). */
export function marketCountryOptionName(
  option: string,
  t: (key: string, params: string[]) => string,
  locale: string
): string | null {
  const country = marketCountryOf(option);
  return country ? t("mira.market.countryOption", [countryName(country, locale)]) : null;
}

/**
 * Every country any of the documents lists, in IRES Cloud's order (most companies first): the landing page's
 * menu, whose cards come from every section, while IRES Cloud lists a country only where it has a figure.
 */
export function listedMarketCountries(documents: ReadonlyArray<{ countries?: string[] } | null | undefined>): string[] {
  return [...new Set(documents.flatMap((document) => document?.countries ?? []))];
}

/**
 * The Markt a screen or card reads: every country's without a country; with one, that country's document
 * once it is loaded, else none, never every country's under the country's name (while it loads, or where the
 * section has no Markt for that country).
 */
export function marketDocumentForCountry<T extends { country?: string }>(
  country: string | null,
  countryDocument: T | null | undefined,
  allDocument: T | null | undefined
): T | null {
  if (!country) {
    return allDocument ?? null;
  }
  return countryDocument?.country === country ? countryDocument : null;
}
